import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useMembershipContext } from '../context/MembershipContext'
import { CreateLoadSchemaType } from '../schema'
import {
  TEAM_MEMBER_ROLE_TYPES,
  TEAM_MEMBER_USER_TYPES,
  IMotorCarrier,
  IUserMotorCarrier,
} from '../types'
import {
  isDispatcherAssociatedToCarrier,
  findDispatcherById,
  isDriverAssociatedToCarrier,
  getMotorCarrierProp,
} from '../utils/motorCarrierHelpers'
import { dispatchersListSelector } from '../utils/selectors'
import { useFMSAnalytics } from './useFMSAnalytics'

export interface IAssociatedMessage {
  name: string
  isAssociated: boolean
  value: string
}

/**
 * Combines state from various parent sources to validate whether
 * a model is associated to a parent user carrier
 */
export const useMembershipValidation = () => {
  const { userMotorCarriers = [], isUserMotorCarriersLoading } = useMembershipContext()
  const { trackEvent } = useFMSAnalytics()

  const dispatchers = useSelector(dispatchersListSelector)

  const getRoleTypeForCarrier = useCallback(
    (motorCarrierId: string | number): TEAM_MEMBER_ROLE_TYPES | undefined => {
      const membership = getMotorCarrierProp(userMotorCarriers, motorCarrierId, 'membership')
      return membership?.role
    },
    [userMotorCarriers]
  )

  const getUserTypeForCarrier = useCallback(
    (motorCarrierId: string | number): TEAM_MEMBER_USER_TYPES | undefined => {
      if (userMotorCarriers.length === 0) {
        trackEvent('Book Load', 'USER_MOTOR_CARRIERS_EMPTY', {
          motorCarrierId,
          userMotorCarriers,
          isUserMotorCarriersLoading,
        })
      }
      const membership = getMotorCarrierProp(userMotorCarriers, motorCarrierId, 'membership')
      return membership?.userType
    },
    [userMotorCarriers, trackEvent, isUserMotorCarriersLoading]
  )

  /**
   * Helper function to test if current user is admin/employee for a motor carrier
   */
  const isRoleTypeForCarrier = useCallback(
    (motorCarrierId: string | number, role: keyof typeof TEAM_MEMBER_ROLE_TYPES): boolean => {
      const userRole = getRoleTypeForCarrier(motorCarrierId)
      return userRole === TEAM_MEMBER_ROLE_TYPES[role]
    },
    [getRoleTypeForCarrier]
  )

  /**
   * Returns true if selected dispatcher is associated with
   * the selected motor carrier
   */
  const isAssociatedCarrier = useCallback(
    (dispatcherId: string | number, motorCarrierId: string | number): boolean => {
      const dispatcher = dispatchers.find(d => d.id === dispatcherId)
      if (!dispatcher) return false
      return isDispatcherAssociatedToCarrier(dispatcher, motorCarrierId)
    },
    [dispatchers]
  )

  /**
   * Returns true if the current user is an admin
   * for the selected motor carrier
   */
  const isAdminForCarrier = useCallback(
    (motorCarrierId: string | number): boolean => {
      return isRoleTypeForCarrier(motorCarrierId, 'ADMIN')
    },
    [isRoleTypeForCarrier]
  )

  /**
   * Returns an array of messages if any items are not associated with
   * selected carrier
   */
  const getUnassociatedItemsFromLoad = useCallback(
    (
      loadRequest: Pick<CreateLoadSchemaType, 'dispatcherId' | 'motorCarrierId' | 'drivers'>
    ): IAssociatedMessage[] => {
      const { motorCarrierId, dispatcherId, drivers = [] } = loadRequest || {}
      const dispatcher = findDispatcherById(dispatchers, dispatcherId)
      const [driver1, driver2] = drivers || []

      // Set up array of checked items with label, isAssociated, and value
      const messages: IAssociatedMessage[] = []

      if (dispatcher) {
        messages.push({
          name: 'Dispatcher',
          isAssociated: isDispatcherAssociatedToCarrier(dispatcher, motorCarrierId),
          value: dispatcher?.fullName!,
        })
      }

      if (driver1) {
        messages.push({
          name: 'Driver 1',
          isAssociated: isDriverAssociatedToCarrier(driver1, motorCarrierId),
          value: driver1?.name,
        })
      }

      // only validate 2nd driver if it's here
      if (driver2) {
        messages.push({
          name: 'Driver 2',
          isAssociated: isDriverAssociatedToCarrier(driver2, motorCarrierId),
          value: driver2?.name,
        })
      }

      // only keep items that are not associated with carrier
      return messages.filter(({ isAssociated }) => !isAssociated)
    },
    [dispatchers]
  )

  /**
   * Returns true if user is valid type/role for booking and bidding
   */
  const userCanBidAndBook = useCallback(
    (motorCarrierId: string | number): boolean => {
      const userType = getUserTypeForCarrier(motorCarrierId)

      if (!userType) return false

      if (
        userType === TEAM_MEMBER_USER_TYPES.DISPATCHER ||
        userType === TEAM_MEMBER_USER_TYPES.OWNER_OPERATOR
      ) {
        return true
      }

      return false
    },
    [getUserTypeForCarrier]
  )

  /**
   * Helper method to be used by other internal hook functions
   * for finding carriers
   */
  const findMotorCarrierByProp = useCallback(
    <K extends keyof IMotorCarrier>(
      keyName: K,
      value: IMotorCarrier[K]
    ): IUserMotorCarrier | undefined => {
      return userMotorCarriers.find(mc => mc.motorCarrier[keyName] === value)
    },
    [userMotorCarriers]
  )

  return {
    isAssociatedCarrier,
    isAdminForCarrier,
    getUnassociatedItemsFromLoad,
    isRoleTypeForCarrier,
    getRoleTypeForCarrier,
    getUserTypeForCarrier,
    userCanBidAndBook,
    findMotorCarrierByProp,
  }
}
